import React from "react";

import { useT } from "../i18n";
import { useTemplateVars } from "../i18n/vars";
import { company } from "../config";
import Seo from "../components/Seo";
import Reveal from "../components/motion/Reveal";
import PageHero from "../components/blocks/PageHero";
import ContactForm from "../components/blocks/ContactForm";
import { IndexedList } from "../components/blocks/lists";

// Guidance and verifiable company details on the left, the form on the right.
//
// Every detail row is conditional. An empty phone in .env means no phone row
// at all, which reads better than a label with nothing after it.

function DetailRow({ label, children }) {
  return (
    <div className="row-t flex flex-col gap-1 py-4 sm:flex-row sm:gap-5">
      <span className="font-caption text-[10.5px] uppercase leading-[1.9] tracking-[1.3px] text-fg-muted sm:w-[140px] sm:shrink-0">
        {label}
      </span>
      <span className="min-w-0 flex-1 font-mono text-[13.5px] leading-[1.6] text-fg-body">
        {children}
      </span>
    </div>
  );
}

export default function ContactPage() {
  const { t, tList } = useT();
  const vars = useTemplateVars();

  return (
    <>
      <Seo title={t("contact.seoTitle")} description={t("contact.seoDescription", vars)} />

      <PageHero
        eyebrow={t("contact.eyebrow")}
        title={t("contact.title")}
        lede={t("contact.lede")}
      />

      <section className="section-t pb-20 pt-14 lg:pb-24 lg:pt-16">
        <div className="shell flex flex-col gap-12 lg:flex-row lg:gap-20">
          <div className="flex flex-col gap-12 lg:w-[460px] lg:shrink-0">
            <div className="flex flex-col gap-[18px]">
              <Reveal>
                <h2 className="t-section-title">{t("contact.includeTitle")}</h2>
              </Reveal>
              <IndexedList items={tList("contact.includeItems")} size="dense" />
              <Reveal>
                <p className="font-caption text-[14px] leading-[1.6] text-fg-muted">
                  {t("contact.responseNote")}
                </p>
              </Reveal>
            </div>

            <div className="flex flex-col gap-[18px]">
              <Reveal>
                <h2 className="t-section-title">{t("contact.directTitle")}</h2>
              </Reveal>
              {/* No email row on purpose. The form is the contact channel on
                  this page; the address itself appears only in the Terms and
                  Privacy Policy. Do not re-add it here. */}
              <Reveal className="rows flex flex-col">
                <DetailRow label={t("contact.entityLabel")}>{company.legalName}</DetailRow>
                {company.phone && (
                  <DetailRow label={t("contact.phoneLabel")}>
                    <a
                      href={`tel:${company.phone.replace(/\s+/g, "")}`}
                      className="link-quiet !text-fg-body"
                    >
                      {company.phone}
                    </a>
                  </DetailRow>
                )}
                {company.address && (
                  <DetailRow label={t("contact.addressLabel")}>{company.address}</DetailRow>
                )}
              </Reveal>
            </div>
          </div>

          <Reveal className="min-w-0 flex-1" delay={80}>
            <ContactForm />
          </Reveal>
        </div>
      </section>
    </>
  );
}
